import React, { useState } from 'react';
import { Table as BsTable, Collapse } from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

import SubtleBadge from 'components/common/SubtleBadge';

const getLineClassification = line => {
  if (line.counted_quantity === null)
    return { label: 'Not Entered', color: 'secondary' };
  if (line.counted_quantity === line.system_quantity)
    return { label: 'Match', color: 'success' };
  if (line.over_result) {
    const or = line.over_result;
    if ('rejected' in or && or.remaining === 0)
      return { label: 'Fully Rejected', color: 'success' };
    if ('rejected' in or && or.remaining > 0)
      return { label: 'Partially Rejected', color: 'danger' };
    return { label: 'Excess (Reject Required)', color: 'danger' };
  }
  if (line.safe_prediction_result)
    return { label: 'Adjustable', color: 'primary' };
  return { label: 'No Change', color: 'secondary' };
};

const PredictionDetail = ({ line }) => {
  const { safe_prediction_result, over_result } = line;

  return (
    <div className="p-3 bg-light small">
      {safe_prediction_result && (
        <div className="mb-2">
          <strong>ProductBatch Adjustment Plan:</strong>
          {safe_prediction_result.adjustments.map((adj, i) => {
            const diff = adj.quantity_to - adj.quantity_from;
            const diffLabel =
              diff === 0
                ? '(No Change)'
                : diff > 0
                ? `(+${diff})`
                : `(${diff})`;
            return (
              <div key={i} className="ms-2">
                PB #{adj.product_batch_id}: {adj.quantity_from} →{' '}
                {adj.quantity_to} {diffLabel}
              </div>
            );
          })}
        </div>
      )}
      {over_result && (
        <div
          className={`fw-semibold ${
            'rejected' in over_result && over_result.remaining === 0
              ? 'text-success'
              : 'text-warning'
          }`}
        >
          {'rejected' in over_result ? (
            over_result.remaining > 0 ? (
              <>
                ⚠ Rejected {over_result.rejected}/{over_result.excess} —{' '}
                {over_result.remaining} remaining (manual handling required)
              </>
            ) : (
              <>
                ✓ Rejected {over_result.rejected}/{over_result.excess} — fully
                resolved
              </>
            )
          ) : (
            <>
              ⚠ Excess: {over_result.excess} — {over_result.excess} CREATED
              order(s) will be auto-rejected in Phase 2
            </>
          )}
        </div>
      )}
    </div>
  );
};

const StocktakingLinesTable = ({ lines, columns }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const table = useReactTable({
    data: lines,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const toggleRow = rowId => {
    setExpandedRow(prev => (prev === rowId ? null : rowId));
  };

  return (
    <div className="table-responsive" style={{ fontSize: '0.75rem' }}>
      <BsTable bordered hover size="sm" className="mb-0">
        <thead className="table-light">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              <th style={{ width: 32 }} className="text-center py-1">
                #
              </th>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  className={`py-1 ${
                    header.column.columnDef.meta?.headerProps?.className || ''
                  }`}
                  style={{
                    minWidth: header.column.columnDef.meta?.minWidth,
                    maxWidth: header.column.columnDef.meta?.maxWidth,
                  }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
              <th className="text-center py-1">Classification</th>
            </tr>
          ))}
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + 2}
                className="text-center text-muted py-3"
              >
                No items
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row, idx) => {
              const line = row.original;
              const classification = getLineClassification(line);
              const hasDetail = line.safe_prediction_result || line.over_result;
              const isExpanded = expandedRow === row.id;

              return (
                <React.Fragment key={row.id}>
                  <tr
                    onClick={() => hasDetail && toggleRow(row.id)}
                    style={{ cursor: hasDetail ? 'pointer' : 'default' }}
                    className={isExpanded ? 'table-active' : ''}
                  >
                    <td className="text-center text-muted py-1">{idx + 1}</td>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className={`py-1 ${
                          cell.column.columnDef.meta?.cellProps?.className || ''
                        }`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                    <td className="text-center py-1">
                      <SubtleBadge pill bg={classification.color}>
                        {classification.label}
                      </SubtleBadge>
                    </td>
                  </tr>
                  {hasDetail && (
                    <tr>
                      <td colSpan={columns.length + 2} className="p-0 border-0">
                        <Collapse in={isExpanded}>
                          <div>
                            <PredictionDetail line={line} />
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </BsTable>
    </div>
  );
};

export default StocktakingLinesTable;
